import { useState } from 'react';

const EMPTY_ACTS = () => [
  { bullets: [{ text: '', details: ['', '', ''] }, { text: '', details: ['', '', ''] }, { text: '', details: ['', '', ''] }] },
  { bullets: [{ text: '', details: ['', '', ''] }, { text: '', details: ['', '', ''] }, { text: '', details: ['', '', ''] }] },
  { bullets: [{ text: '', details: ['', '', ''] }, { text: '', details: ['', '', ''] }, { text: '', details: ['', '', ''] }] },
];

export function StoryEditor({ story, onSave, onCancel, onDelete }) {
  const [title, setTitle] = useState(story?.title || '');
  const [role, setRole] = useState(story?.role || '');
  const [company, setCompany] = useState(story?.company || '');
  const [year, setYear] = useState(story?.year || '');
  const [notes, setNotes] = useState(story?.notes || '');
  const [actNames, setActNames] = useState(story?.actNames || ['Problem', 'Action', 'Result']);
  const [acts, setActs] = useState(story?.acts || EMPTY_ACTS());
  const [saving, setSaving] = useState(false);

  function updateActName(idx, value) {
    setActNames((n) => n.map((v, i) => i === idx ? value : v));
  }

  function updateBullet(actIdx, bulletIdx, value) {
    setActs((a) => a.map((act, ai) => ai === actIdx
      ? { ...act, bullets: act.bullets.map((b, bi) => bi === bulletIdx ? { ...b, text: value } : b) }
      : act
    ));
  }

  function updateDetail(actIdx, bulletIdx, detailIdx, value) {
    setActs((a) => a.map((act, ai) => ai === actIdx
      ? { ...act, bullets: act.bullets.map((b, bi) => bi === bulletIdx
          ? { ...b, details: b.details.map((d, di) => di === detailIdx ? value : d) }
          : b
        )}
      : act
    ));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    await onSave({ title: title.trim(), role: role.trim(), company: company.trim(), year: year.trim(), notes: notes.trim(), actNames, acts });
    setSaving(false);
  }

  return (
    <div className="p-6 max-w-4xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-slate-900">{story ? 'Edit Story' : 'New Story'}</h1>
          <div className="flex gap-2">
            {onDelete && (
              <button type="button" onClick={onDelete} className="rounded px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Delete</button>
            )}
            <button type="button" onClick={onCancel} className="rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
            <button type="submit" disabled={saving} className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Story Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="The time I saved $2M" className="w-full rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Role / Context</label>
            <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Cloud Architect" className="w-full rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Corp" className="w-full rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Year</label>
            <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2024" className="w-full rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Context, tags, or which interviews this story fits…" rows={2} className="w-full rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((actIdx) => (
            <div key={actIdx} className="rounded border border-slate-200 p-4 space-y-3">
              <input
                value={actNames[actIdx]}
                onChange={(e) => updateActName(actIdx, e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-base font-medium focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {acts[actIdx].bullets.map((bullet, bIdx) => (
                <div key={bIdx} className="space-y-1">
                  <input
                    value={bullet.text}
                    onChange={(e) => updateBullet(actIdx, bIdx, e.target.value)}
                    placeholder={`Bullet ${bIdx + 1}`}
                    className="w-full rounded border border-slate-200 px-2 py-1 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  {bullet.details.map((detail, dIdx) => (
                    <input
                      key={dIdx}
                      value={detail}
                      onChange={(e) => updateDetail(actIdx, bIdx, dIdx, e.target.value)}
                      placeholder={`Detail ${dIdx + 1}`}
                      className="w-full rounded border border-slate-100 px-2 py-0.5 text-base text-slate-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ml-2"
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </form>
    </div>
  );
}
